import React from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { CheckoutPricing } from '@/types';
import { COUNTRY_OPTIONS } from '@/constants';
import { formatCardNumber, formatExpiry } from '@/lib/formatters';

interface CardDetails {
  number: string;
  expiry: string;
  cvc: string;
  cardholderName: string;
}

interface BillingAddress {
  address1: string;
  city: string;
  postalCode: string;
  country: string;
}

interface ManualPaymentPanelProps {
  hasService: boolean;
  cardDetails: CardDetails;
  onCardDetailsChange: (details: CardDetails) => void;
  billingAddress: BillingAddress;
  onBillingAddressChange: (address: BillingAddress) => void;
  pricing: CheckoutPricing;
}

export const ManualPaymentPanel: React.FC<ManualPaymentPanelProps> = ({
  hasService,
  cardDetails,
  onCardDetailsChange,
  billingAddress,
  onBillingAddressChange,
  pricing
}) => {
  if (!hasService) return null;

  return (
    <div className="mt-6 pt-6 border-t">
      <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10 mb-4 space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-widest text-teal-400 flex items-center gap-2">
            <Lock className="w-3 h-3" /> Secure Payment
          </h4>
          <div className="flex items-center gap-1 text-[8px] font-black text-slate-400 bg-white/5 px-1.5 py-0.5 rounded w-fit">
            <ShieldCheck className="w-2.5 h-2.5 text-teal-500" /> OPAYO SECURE
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <div className="bg-white/5 p-4 rounded-xl border border-white/10">
            <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">
              Manual Transaction Entry
            </div>
            <div className="space-y-3">
              <input
                type="text"
                className="w-full bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                placeholder="Card Number"
                maxLength={19}
                value={cardDetails.number}
                onChange={(e) =>
                  onCardDetailsChange({
                    ...cardDetails,
                    number: formatCardNumber(e.target.value, 16)
                  })
                }
              />
              <input
                type="text"
                className="w-full bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                placeholder="Cardholder Name"
                maxLength={50}
                value={cardDetails.cardholderName}
                onChange={(e) =>
                  onCardDetailsChange({ ...cardDetails, cardholderName: e.target.value })
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  className="bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                  placeholder="MM / YY"
                  maxLength={5}
                  value={cardDetails.expiry}
                  onChange={(e) =>
                    onCardDetailsChange({
                      ...cardDetails,
                      expiry: formatExpiry(e.target.value)
                    })
                  }
                />
                <input
                  type="text"
                  className="bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                  placeholder="CVC"
                  maxLength={4}
                  value={cardDetails.cvc}
                  onChange={(e) =>
                    onCardDetailsChange({
                      ...cardDetails,
                      cvc: e.target.value.replace(/[^0-9]/g, '')
                    })
                  }
                />
              </div>
              <input
                type="text"
                className="w-full bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                placeholder="Billing Address"
                value={billingAddress.address1}
                onChange={(e) =>
                  onBillingAddressChange({ ...billingAddress, address1: e.target.value })
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  className="bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                  placeholder="City"
                  value={billingAddress.city}
                  onChange={(e) =>
                    onBillingAddressChange({ ...billingAddress, city: e.target.value })
                  }
                />
                <input
                  type="text"
                  className="bg-white/10 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                  placeholder="Postcode"
                  value={billingAddress.postalCode}
                  onChange={(e) =>
                    onBillingAddressChange({ ...billingAddress, postalCode: e.target.value })
                  }
                />
              </div>
              <select
                className="w-full bg-slate-900 h-10 rounded border border-white/20 px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                style={{ colorScheme: 'dark' }}
                value={billingAddress.country}
                onChange={(e) =>
                  onBillingAddressChange({ ...billingAddress, country: e.target.value })
                }
              >
                {COUNTRY_OPTIONS.map((option) => (
                  <option
                    key={option.code}
                    value={option.code}
                    style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}
                  >
                    {option.label} ({option.code})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="pt-2 border-t border-white/10 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="font-medium text-slate-400">Subtotal</span>
              <span className="font-bold text-white">£{pricing.subtotalAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-medium text-slate-400">
                VAT ({pricing.vatRatePercent.toFixed(2)}%)
              </span>
              <span className="font-bold text-white">£{pricing.vatAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/10">
              <span className="text-sm font-medium text-slate-400">Total</span>
              <span className="text-2xl font-black text-white">
                £{pricing.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
